import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { EntryApiAdapter } from '../../../adapters/EntryApiAdapter';
import { ReceiptApiAdapter } from '../../adapters';
import { ProductDimensionsApiAdapter } from '../../adapters/ProductDimensionsApiAdapter';
import { SeparationTimeProduct } from '../../adapters/ReceiptApiAdapter';
import { useMovements, useReceiptCarts, useSeparationTimes } from '../../hooks';
import { useReceiveFlow } from '../../hooks/useReceiveFlow';
import { RejectionData } from '../../models/Receive';
import { useToast } from '@shared/hooks/use-toast';
import {
  Breadcrumb,
  CartsList,
  EntryInfo,
  EntryProductsList,
  KPICards,
  ReceivedMovementsList,
  RequirementsList,
} from '../display';
import {
  DispatchModal,
  DispatchModalRef,
  CancelReceiptModal,
  CancelReceiptModalRef,
  FinalizeReceiptModal,
  FinalizeReceiptModalRef,
  LocationOrdersModal,
  LocationOrdersModalRef,
  MovementsModal,
  MovementsModalRef,
  QuantityConfirm,
  ReceiptNotesModal,
  ReceiptNotesModalRef,
  RejectionModal,
  RejectionModalRef,
  RequirementsModal,
  RequirementsModalRef,
  ScannerInput,
  SeparationTimeModal,
  SeparationTimeModalRef,
  ProductDimensionsModal,
  ProductDimensionsModalRef,
} from '../interaction';

export const Main: React.FC = () => {
  const { entryId: entryIdParam, receiptId: receiptIdParam } = useParams<{
    entryId: string;
    receiptId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation('merchandiseV3');
  const { toast } = useToast();
  const { canAccess } = usePermissions();

  const entryId = entryIdParam ? parseInt(entryIdParam, 10) : 0;
  const receiptId = receiptIdParam ? parseInt(receiptIdParam, 10) : 0;

  // Refs de modales
  const rejectionModalRef = useRef<RejectionModalRef>(null);
  const dispatchModalRef = useRef<DispatchModalRef>(null);
  const locationOrdersModalRef = useRef<LocationOrdersModalRef>(null);
  const requirementsModalRef = useRef<RequirementsModalRef>(null);
  const movementsModalRef = useRef<MovementsModalRef>(null);
  const finalizeReceiptModalRef = useRef<FinalizeReceiptModalRef>(null);
  const cancelReceiptModalRef = useRef<CancelReceiptModalRef>(null);
  const receiptNotesModalRef = useRef<ReceiptNotesModalRef>(null);
  const separationTimeModalRef = useRef<SeparationTimeModalRef>(null);
  const productDimensionsModalRef = useRef<ProductDimensionsModalRef>(null);
  const [isRefreshingMainData, setIsRefreshingMainData] = useState(false);

  // Track which requirement was just created so we can reopen modal after refetch
  const [pendingRequirementCode, setPendingRequirementCode] = useState<string | null>(null);

  const canManageReceiptNotes = canAccess(['tenant', 'tenant_3pl'], ['merchandises.receipt']);

  // Hook principal del flujo
  const {
    entry,
    entryLines,
    entryProducts,
    isLoadingEntry,
    entryError,
    refetchEntry,
    state,
    scannedProducts,
    pendingProduct,
    isReceiving,
    cancelPendingProduct,
    acceptProduct,
    rejectProduct,
    dispatchCart,
    completeReceipt,
    cancelReceipt,
    processScan,
    goBack,
  } = useReceiveFlow(entryId, receiptId);

  // Query para obtener el receipt completo
  const httpClient = createHttpClient(apiClientWH);
  const entryAdapter = new EntryApiAdapter(httpClient);

  const queryClient = useQueryClient();

  const {
    data: receipt,
    isLoading: isLoadingReceipt,
    error: receiptError,
    refetch: refetchReceipt,
  } = useQuery({
    queryKey: ['entry-receipt', entryId, receiptId],
    queryFn: () => entryAdapter.getReceipt(entryId, receiptId),
    enabled: entryId > 0 && receiptId > 0,
  });

  // Movimientos y carritos: necesarios también con albarán COMPLETADA (solo lectura)
  const canLoadReceiptDetails = Boolean(receipt);
  const {
    data: movements,
    isLoading: isLoadingMovements,
    refetch: refetchMovements,
  } = useMovements(entryId, receiptId, canLoadReceiptDetails);
  const { data: receiptCartsData, isLoading: isLoadingReceiptCarts } = useReceiptCarts(
    entryId,
    receiptId,
    canLoadReceiptDetails,
  );
  const { registerSeparationTime, updateSeparationTime, isRegistering } = useSeparationTimes(entryId, receiptId);

  // Watch receipt changes after invalidation/refetch
  useEffect(() => {
    if (receipt) {
      // If we have a pending requirement code and receipt now has more requirements, reopen modal with fresh data
      if (
        pendingRequirementCode &&
        (receipt.requirements || []).some((r) => r.requirement_code === pendingRequirementCode)
      ) {
        const isCompleted = receipt.status === 'COMPLETADA';
        requirementsModalRef.current?.open(
          receipt.requirements || [],
          async (reqCode: string, attachments?: File[]) => {
            const httpClient = createHttpClient(apiClientWH);
            const receiptAdapter = new ReceiptApiAdapter(httpClient);
            await receiptAdapter.createRequirement(entryId, receiptId, reqCode, attachments);
            queryClient.invalidateQueries({ queryKey: ['entry-receipt', entryId, receiptId] });
          },
          isCompleted,
        );
        setPendingRequirementCode(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt]);

  // Mapa producto -> carrito(s) asignado(s) a partir de movimientos aceptados (para mostrar "en qué carrito quedó" cada producto)
  const productCartAssignments = React.useMemo(() => {
    const movementsList = Array.isArray(movements) ? movements : [];
    const carts = receiptCartsData?.data ?? [];
    const map: Record<number, { code: string; qty: number }[]> = {};
    for (const m of movementsList) {
      if (m.movement_type !== 'ACCEPTED') continue;
      const code = m.location?.code ?? carts.find((c) => c.id === m.location_id)?.code ?? `#${m.location_id}`;
      if (!map[m.product_id]) map[m.product_id] = [];
      const existing = map[m.product_id].find((x) => x.code === code);
      if (existing) existing.qty += m.qty;
      else map[m.product_id].push({ code, qty: m.qty });
    }
    return map;
  }, [movements, receiptCartsData?.data]);

  // Mutation para rechazar producto
  const rejectProductMutation = useMutation({
    mutationFn: (rejectionData: RejectionData) => rejectProduct(rejectionData),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Éxito',
          description: result.message,
        });
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || t('receive.error.reject_product', 'Error al rechazar el producto'),
        variant: 'destructive',
      });
    },
  });

  // Mutation para despachar carrito
  const dispatchCartMutation = useMutation({
    mutationFn: ({
      locationId,
      dispatchType,
      lines,
    }: {
      locationId: number;
      dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER';
      lines?: Array<{
        group_code: string;
        product_id: number;
        qty_expected: number;
        work_type: 'LABELING' | 'BAGGING' | 'BOTH';
      }>;
    }) => dispatchCart(locationId, dispatchType, lines),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Éxito',
          description: result.message,
        });
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || t('receive.error.dispatch_cart', 'Error al despachar el carrito'),
        variant: 'destructive',
      });
    },
  });

  // Mutation para finalizar recepción
  const finalizeReceiptMutation = useMutation({
    mutationFn: () => completeReceipt(),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Éxito',
          description: result.message,
        });
        refetchReceipt();
        refetchEntry();
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || t('receive.error.finalize', 'Error al finalizar la recepción'),
        variant: 'destructive',
      });
    },
  });

  // Mutation para cancelar recepción
  const cancelReceiptMutation = useMutation({
    mutationFn: (cancelReason: string) => cancelReceipt(cancelReason),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: 'Éxito',
          description: result.message,
        });
        refetchReceipt();
        refetchEntry();
        navigate('/merchandise');
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || t('receive.error.cancel', 'Error al cancelar la recepción'),
        variant: 'destructive',
      });
    },
  });

  // Handlers

  const handleRejectProduct = (productId: number, qty: number) => {
    const entryLine = entryLines.find((line) => line.product_id === productId);
    if (!entryLine) return;

    const buildImageUrl = (image?: string | null): string => {
      if (!image) return '';
      if (image.startsWith('http')) return image;
      return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${image}`;
    };

    rejectionModalRef.current?.open(
      {
        productId,
        productName: entryLine.product?.name || `Producto ${productId}`,
        productCode: entryLine.product?.code || '',
        productImage: buildImageUrl(entryLine.product?.image_hash || entryLine.product?.img),
        scannedQty: qty,
        maxRejectQty: qty,
      },
      async (rejectionData: RejectionData) => {
        await rejectProductMutation.mutateAsync(rejectionData);
        cancelPendingProduct();
      },
    );
  };

  const handleOpenDispatchModal = () => {
    locationOrdersModalRef.current?.open(entryId, receiptId);
  };

  const handleCreateLocationOrder = () => {
    if (state.validatedCart) {
      const entryProducts = state.validatedCart.stock_location_carts?.filter((slc) => slc.entry_id === entryId) || [];

      const products = entryProducts.map((slc) => ({
        product_id: slc.product_id,
        product_name: slc.product?.name || `Producto ${slc.product_id}`,
        product_code: slc.product?.code,
        product_image: slc.product?.image,
        qty: slc.qty,
      }));

      const totalQty = entryProducts.reduce((sum, slc) => sum + slc.qty, 0);

      dispatchModalRef.current?.open(
        [
          {
            location_id: state.validatedCart.id,
            location_code: state.validatedCart.code,
            pending_movements: entryProducts.length,
            total_qty: totalQty,
            products: products,
          },
        ],
        async (
          locationId: number,
          dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
          lines?: Array<{
            group_code: string;
            product_id: number;
            qty_expected: number;
            work_type: 'LABELING' | 'BAGGING' | 'BOTH';
          }>,
        ) => {
          return await dispatchCartMutation.mutateAsync({ locationId, dispatchType, lines });
        },
        entryId,
        receiptId,
      );
    }
  };

  const handleOpenFinalizeModal = () => {
    if (receipt && movements) {
      const movementsArray = Array.isArray(movements) ? movements : [];
      const totalMovements = movementsArray.filter((m) => m.movement_type === 'ACCEPTED').length;
      const dispatchedMovements = movementsArray.filter(
        (m) => m.movement_type === 'ACCEPTED' && m.dispatch_status === 'DISPATCHED',
      ).length;

      finalizeReceiptModalRef.current?.open(
        receipt.code,
        totalMovements,
        dispatchedMovements,
        receipt.requirements || [],
        async () => {
          await finalizeReceiptMutation.mutateAsync();
        },
        () => {
          finalizeReceiptModalRef.current?.close();
          handleOpenRequirementsModal();
        },
      );
    }
  };

  const handleOpenCancelModal = () => {
    if (!receipt) return;

    cancelReceiptModalRef.current?.open(receipt.code, async (cancelReason: string) => {
      await cancelReceiptMutation.mutateAsync(cancelReason);
    });
  };

  const handleOpenRequirementsModal = () => {
    if (receipt) {
      const isCompleted = receipt.status === 'COMPLETADA';

      requirementsModalRef.current?.open(
        receipt.requirements || [],
        async (requirementCode: string, attachments?: File[]) => {
          const httpClient = createHttpClient(apiClientWH);
          const receiptAdapter = new ReceiptApiAdapter(httpClient);
          setPendingRequirementCode(requirementCode);
          await receiptAdapter.createRequirement(entryId, receiptId, requirementCode, attachments);
          queryClient.invalidateQueries({ queryKey: ['entry-receipt', entryId, receiptId] });
        },
        isCompleted,
      );
    }
  };

  const handleOpenMovementsModal = () => {
    if (movements) {
      movementsModalRef.current?.open(movements);
    }
  };

  const handleOpenReceiptNotesModal = () => {
    receiptNotesModalRef.current?.open();
  };

  const handleMovementDeleted = async () => {
    setIsRefreshingMainData(true);
    try {
      await Promise.all([refetchMovements(), refetchReceipt(), refetchEntry()]);
      cancelPendingProduct();
      if (state.currentStep === 'products') {
        goBack();
      }
    } finally {
      setIsRefreshingMainData(false);
    }
  };

  // Validación obligatoria de dimensiones antes de permitir la recepción del producto.
  const handleScanWithDimensions = async (code: string): Promise<{ success: boolean; message: string }> => {
    try {
      if (state.currentStep === 'products') {
        const scannedCode = code.trim().toLowerCase();
        const entryLine = entryLines.find((line) => line.product?.code?.toLowerCase() === scannedCode);

        if (entryLine?.product_id) {
          const dimensionsAdapter = new ProductDimensionsApiAdapter(httpClient);
          const product = await dimensionsAdapter.getById(entryLine.product_id);
          const hasValue = (value: unknown): boolean => {
            if (value === null || value === undefined) return false;
            const normalized = String(value).trim();
            if (!normalized) return false;
            const numericValue = Number(normalized);
            return Number.isFinite(numericValue) && numericValue > 0;
          };
          const hasLength = hasValue(product.length);
          const hasHeight = hasValue(product.heigth);
          const hasWidth = hasValue(product.width);
          const hasWeight = hasValue(product.weight);
          const hasCategory = (() => {
            const normalized = String(product.category_id ?? '').trim();
            if (!normalized) return false;
            const parsed = Number(normalized);
            return Number.isFinite(parsed) && parsed > 0;
          })();

          if (!hasLength || !hasHeight || !hasWidth || !hasWeight || !hasCategory) {
            productDimensionsModalRef.current?.open(entryLine.product_id, async () => {
              const scanResult = await processScan(code);
              if (scanResult.message) {
                toast({
                  title: scanResult.success ? 'Éxito' : 'Error',
                  description: scanResult.message,
                  variant: scanResult.success ? 'default' : 'destructive',
                });
              }
            });

            return { success: true, message: '' };
          }
        }
      }

      return processScan(code);
    } catch (error: any) {
      console.error('🔍 handleScanWithDimensions - Error:', error);
      return {
        success: false,
        message: error?.message || t('receive.error.accept_product', 'Error al aceptar el producto'),
      };
    }
  };

  const handleOpenSeparationTimesModal = () => {
    if (entryLines && movements && receipt) {
      separationTimeModalRef.current?.open(
        entryLines,
        movements,
        receipt.separation_times,
        async (products: SeparationTimeProduct[], timeMinutes: number) => {
          await registerSeparationTime({ products, time_minutes: timeMinutes });
          toast({
            title: 'Éxito',
            description: t('separation_time.success', 'Tiempo de separación registrado correctamente'),
          });
        },
        async () => {
          await refetchReceipt();
          await refetchEntry();
          const updatedReceipt = await entryAdapter.getReceipt(entryId, receiptId);
          return updatedReceipt.separation_times;
        },
        async (separationTimeId: number, products: SeparationTimeProduct[], timeMinutes: number) => {
          await updateSeparationTime({
            separationTimeId,
            data: { products, time_minutes: timeMinutes },
          });
          toast({
            title: 'Éxito',
            description: t('separation_time.update_success', 'Tiempo de separación actualizado correctamente'),
          });
        },
      );
    }
  };

  // Validación del ID
  if (!entryId || entryId <= 0 || !receiptId || receiptId <= 0) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {t('receive.error.invalid_ids', 'ID de entrada o recepción inválido')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('receive.error.invalid_ids_description', 'Por favor, accede a una recepción válida desde el listado.')}
          </p>
          <button
            onClick={() => navigate('/merchandise')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('receive.error.back_to_list', 'Ir al listado')}
          </button>
        </div>
      </div>
    );
  }

  // Estado de carga
  if (isLoadingEntry || isLoadingReceipt) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('receive.loading', 'Cargando recepción...')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error al cargar
  if (entryError || receiptError) {
    console.error('Entry error:', entryError);
    console.error('Receipt error:', receiptError);
    console.error('Receipt:', receipt);
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {t('receive.error.load_error', 'Error al cargar la recepción')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {entryError?.message ||
              receiptError?.message ||
              String(entryError || receiptError) ||
              t('receive.error.not_found', 'Recepción no encontrada')}
          </p>
          <button
            onClick={() => navigate('/merchandise')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('receive.error.back_to_list', 'Ir al listado')}
          </button>
        </div>
      </div>
    );
  }

  // Verificar que tenemos los datos necesarios
  if (!entry || !receipt) {
    console.log('Missing data - entry:', entry, 'receipt:', receipt);
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {t('receive.error.load_error', 'Error al cargar la recepción')}
          </h1>
          <p className="text-muted-foreground mb-6">{t('receive.error.not_found', 'Recepción no encontrada')}</p>
          <button
            onClick={() => navigate('/merchandise')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('receive.error.back_to_list', 'Ir al listado')}
          </button>
        </div>
      </div>
    );
  }

  // Recepción completada - Vista de solo lectura
  if (receipt.status === 'COMPLETADA') {
    return (
      <div
        className={`container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col transition-opacity ${
          isRefreshingMainData ? 'opacity-50' : 'opacity-100'
        }`}
      >
        <Breadcrumb
          entryId={entryId}
          receiptId={receiptId}
          receiptCode={receipt.code}
          receiptStatus={receipt.status}
          onNotesClick={handleOpenReceiptNotesModal}
        />

        <div className="mb-6 bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg
                className="w-8 h-8 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-md font-bold text-green-900 dark:text-green-100 mb-2">
                {t('receive.completed.title', 'Recepción Finalizada')}
              </h2>
              <p className="text-green-800 dark:text-green-200">
                {t(
                  'receive.completed.description',
                  'Esta recepción ha sido finalizada exitosamente. No se pueden procesar más productos ni realizar cambios.',
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Banner de información de entrada */}
        <EntryInfo entry={entry} receipt={receipt} />

        <div className="flex flex-col lg:flex-row flex-1 min-h-0 gap-6">
          <div className="flex flex-col lg:w-1/2 space-y-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t('receive.summary.title', 'Resumen de la Recepción')}
            </h2>
            <KPICards
              cartsCount={receiptCartsData?.data?.length || 0}
              requirementsCount={receipt.requirements?.length || 0}
              movementsCount={movements?.length || 0}
              onCartsClick={handleOpenDispatchModal}
              onRequirementsClick={handleOpenRequirementsModal}
              onMovementsClick={handleOpenMovementsModal}
            />
            {receipt.requirements && receipt.requirements.length > 0 && (
              <RequirementsList requirements={receipt.requirements} />
            )}
            {receipt.separation_times && receipt.separation_times.length > 0 && (
              <button
                onClick={handleOpenSeparationTimesModal}
                className="w-full px-3 py-2 bg-background border border-border hover:border-primary/50 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <Clock className="w-4 h-4" />
                <span>
                  {t('separation_time.view_times', 'Ver Tiempos Registrados')} ({receipt.separation_times.length})
                </span>
              </button>
            )}
          </div>

          <div className="flex flex-col lg:w-1/2 min-h-0">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                {t('receive.movements.title', 'Movimientos Registrados')}
              </h2>
            </div>
            <div className="flex-1 min-h-0">
              <ReceivedMovementsList movements={movements || []} isLoading={isLoadingMovements} showTitle={false} />
            </div>
          </div>
        </div>

        {/* Modales */}
        <LocationOrdersModal ref={locationOrdersModalRef} />
        <RequirementsModal ref={requirementsModalRef} />
        <MovementsModal
          ref={movementsModalRef}
          entryId={entryId}
          receiptId={receiptId}
          onMovementDeleted={handleMovementDeleted}
        />
        <SeparationTimeModal ref={separationTimeModalRef} />
        <ReceiptNotesModal
          ref={receiptNotesModalRef}
          entryId={entryId}
          receiptId={receiptId}
          canManageNotes={canManageReceiptNotes}
        />
      </div>
    );
  }

  // Obtener descripción del paso actual
  const getStepDescription = () => {
    switch (state.currentStep) {
      case 'cart':
        return t('receive.steps.cart.description', 'Escanea el código del carrito para comenzar');
      case 'products':
        return t('receive.steps.products.description', 'Escanea los productos de la entrada');
      default:
        return '';
    }
  };

  const getScannerPlaceholder = () => {
    if (state.currentStep === 'cart') {
      return t('receive.steps.cart.placeholder', 'Escanea o escribe el código del carrito...');
    }
    return t('receive.steps.products.placeholder', 'Escanea o escribe el código del producto...');
  };

  return (
    <div
      className={`container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col transition-opacity ${
        isRefreshingMainData ? 'opacity-50' : 'opacity-100'
      }`}
    >
      <Breadcrumb
        receiptId={receiptId}
        receiptCode={receipt.code}
        receiptStatus={receipt.status}
        onNotesClick={handleOpenReceiptNotesModal}
        onCancelClick={handleOpenCancelModal}
        onFinalizeClick={handleOpenFinalizeModal}
        isCancelDisabled={!!movements && movements.length > 0}
        cancelTooltip={
          movements && movements.length > 0
            ? t('receive.cancel.disabled_tooltip', 'Solo se puede cancelar una recepción sin movimientos')
            : undefined
        }
        isFinalizeDisabled={!!state.validatedCart || !movements || movements.length === 0}
        finalizeTooltip={
          !movements || movements.length === 0
            ? t('receive.finalize.disabled_tooltip', 'Debe recepcionar al menos un ítem para finalizar')
            : state.validatedCart
              ? t('receive.finalize.cart_active_tooltip', 'Debe terminar de procesar el carrito actual para finalizar')
              : undefined
        }
      />

      {/* Banner de información de entrada */}
      {receipt && <EntryInfo entry={entry} receipt={receipt} />}

      {/* Panel principal */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 gap-6">
        {/* Panel izquierdo - Scanner y acciones */}
        <div className="flex flex-col lg:w-1/2 space-y-4">
          {/* Banner del carrito activo */}
          {state.validatedCart &&
            (() => {
              // Calcular productos del carrito de esta entrada
              // Filtrar solo los productos que pertenecen a esta entrada (entry_id)
              const entryProducts =
                state.validatedCart.stock_location_carts?.filter((slc) => slc.entry_id === entryId) || [];
              const totalProducts = entryProducts.length;
              const totalQuantity = entryProducts.reduce((sum, slc) => sum + slc.qty, 0);
              const canCreateLocationOrder = totalProducts > 0;

              return (
                <div className="bg-background border border-border rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="bg-green-50 dark:bg-green-950/20 border-b border-green-200 dark:border-green-800 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 dark:bg-green-900/40 rounded-lg p-2">
                          <svg
                            className="w-5 h-5 text-green-600 dark:text-green-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs text-green-600 dark:text-green-400 font-medium mb-0.5">
                            {t('receive.active_cart.title', 'Carrito Activo')}
                          </div>
                          <div className="text-lg font-bold text-foreground font-mono">{state.validatedCart.code}</div>
                        </div>
                      </div>
                      <button
                        onClick={goBack}
                        className="px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 rounded-md transition-colors"
                      >
                        {t('receive.active_cart.change', 'Cambiar')}
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-muted-foreground"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                          </svg>
                          <span className="text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">{totalProducts}</span>{' '}
                            {totalProducts === 1
                              ? t('receive.active_cart.product', 'producto')
                              : t('receive.active_cart.products', 'productos')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-muted-foreground"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                          <span className="text-sm text-muted-foreground">
                            <span className="font-semibold text-foreground">{totalQuantity}</span>{' '}
                            {t('receive.active_cart.units', 'unidades')}
                          </span>
                        </div>
                      </div>
                      {canCreateLocationOrder && (
                        <button
                          onClick={handleCreateLocationOrder}
                          className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-md transition-colors flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {t('receive.active_cart.dispatch', 'Despachar')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Scanner o QuantityConfirm según el estado */}
          {pendingProduct ? (
            <QuantityConfirm
              pendingProduct={pendingProduct}
              cartCode={state.validatedCart?.code}
              onAccept={async (productId, locationCode, qty, batchId) => {
                const result = await acceptProduct(productId, locationCode, qty, batchId);
                if (result.success) {
                  toast({
                    title: 'Éxito',
                    description: result.message,
                  });
                  cancelPendingProduct();
                } else {
                  toast({
                    title: 'Error',
                    description: result.message,
                    variant: 'destructive',
                  });
                }
                return result;
              }}
              onReject={(productId, qty) => {
                handleRejectProduct(productId, qty);
              }}
              onCancel={cancelPendingProduct}
            />
          ) : (
            <ScannerInput
              stepDescription={getStepDescription()}
              placeholder={getScannerPlaceholder()}
              onScan={handleScanWithDimensions}
              disabled={state.isProcessing || isReceiving}
              buttonText={
                state.currentStep === 'cart'
                  ? t('receive.scanner.validate_cart', 'Validar Carrito')
                  : t('receive.scanner.scan_product', 'Escanear Producto')
              }
            />
          )}

          {/* Botón volver */}
          {state.currentStep === 'products' && !state.validatedCart && !pendingProduct && (
            <button
              onClick={goBack}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg hover:border-primary transition-colors"
            >
              {t('receive.back', 'Volver a escanear carrito')}
            </button>
          )}

          {/* Lista de carritos (solo cuando no hay carrito activo) */}
          {!state.validatedCart && receiptCartsData && (
            <CartsList carts={receiptCartsData.data || []} isLoading={isLoadingReceiptCarts} />
          )}
        </div>

        {/* Panel derecho - KPIs, Movimientos y Lista de productos */}
        <div className="flex flex-col lg:w-1/2 min-h-0 space-y-4">
          {/* KPIs */}
          <KPICards
            cartsCount={receiptCartsData?.data?.length || 0}
            requirementsCount={receipt.requirements?.length || 0}
            movementsCount={movements?.length || 0}
            onCartsClick={handleOpenDispatchModal}
            onRequirementsClick={handleOpenRequirementsModal}
            onMovementsClick={handleOpenMovementsModal}
          />

          {/* Botón para ver/registrar tiempos de separación */}
          {receipt?.status === 'ABIERTA' && (
            <button
              onClick={handleOpenSeparationTimesModal}
              disabled={isRegistering || !movements || movements.length === 0}
              className="w-full px-3 py-2 bg-background border border-border hover:border-primary/50 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Clock className="w-4 h-4" />
              {isRegistering
                ? t('separation_time.registering', 'Registrando...')
                : receipt.separation_times && receipt.separation_times.length > 0
                  ? t('separation_time.view_times', 'Ver Tiempos Registrados') + ` (${receipt.separation_times.length})`
                  : t('separation_time.register_button', 'Registrar Tiempo de Separación')}
            </button>
          )}

          {/* Lista de productos esperados */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t('receive.products.title', 'Productos de la Entrada')}
            </h2>
            <span className="text-sm text-muted-foreground">
              {entryLines.length} {t('receive.products.count', 'productos')}
            </span>
          </div>
          <div className="flex-1 min-h-0">
            <EntryProductsList
              products={entryProducts}
              scannedProducts={scannedProducts}
              productCartAssignments={productCartAssignments}
            />
          </div>
        </div>
      </div>

      {/* Modales */}
      <RejectionModal ref={rejectionModalRef} />
      <DispatchModal ref={dispatchModalRef} />
      <LocationOrdersModal ref={locationOrdersModalRef} />
      <RequirementsModal ref={requirementsModalRef} />
      <MovementsModal
        ref={movementsModalRef}
        entryId={entryId}
        receiptId={receiptId}
        onMovementDeleted={handleMovementDeleted}
      />
      <FinalizeReceiptModal ref={finalizeReceiptModalRef} />
      <CancelReceiptModal ref={cancelReceiptModalRef} />
      <ReceiptNotesModal
        ref={receiptNotesModalRef}
        entryId={entryId}
        receiptId={receiptId}
        canManageNotes={canManageReceiptNotes}
      />
      <SeparationTimeModal ref={separationTimeModalRef} />
      <ProductDimensionsModal ref={productDimensionsModalRef} httpClient={httpClient} />
    </div>
  );
};
